/**
 * SellerEditForm — RHF + Zod edit form for a seller (STORY-008-007). PATCHes
 * the SHIPPED `/admin/marketplace/sellers/{id}` route with a subset of
 * `SellerUpdate`. Mirrors the seller-dashboard editor's RHF mechanics; only the
 * explicitly-provided fields are sent (empty nullable strings → `null`).
 */
import { useCallback, type ReactElement } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { Button, Field } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import {
  SELLER_VERIFICATION_STATUSES,
  type AdminSeller,
  type AdminSellerUpdate,
} from "@/features/admin/sellers/api";

// Mirrors the backend's `_E164_RE` exactly (SCRUM-147 — see the identical
// fix + full rationale in SellerCreateDialog.tsx): a leading zero right
// after '+' must be rejected client-side too, or a common real-world typo
// (a local number with its leading-zero trunk prefix, just prepended with
// '+' instead of the country code) passes here and fails server-side with
// an opaque error.
const E164_RE = /^\+[1-9]\d{7,14}$/;

const sellerEditSchema = z.object({
  business_name: z.string().min(1, "formError.required").max(200),
  pavilion_number: z.string().min(1, "formError.required").max(64),
  phone_e164: z.string().regex(E164_RE, "formError.phone"),
  whatsapp_phone_e164: z
    .string()
    .max(20)
    .refine((v) => v === "" || E164_RE.test(v), "formError.phone"),
  telegram_handle: z.string().max(64),
  entrance_hint: z.string().max(500),
  verification_status: z.enum(["pending", "verified", "rejected", "suspended"]),
  verification_notes: z.string().max(1000),
});

type SellerEditValues = z.infer<typeof sellerEditSchema>;

function nullIfBlank(v: string): string | null {
  const t = v.trim();
  return t === "" ? null : t;
}

export interface SellerEditFormProps {
  seller: AdminSeller;
  isPending: boolean;
  onSubmit: (body: AdminSellerUpdate) => void;
  onCancel: () => void;
}

const SELECT_CLASS =
  "min-h-[44px] w-full rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]";

export function SellerEditForm({
  seller,
  isPending,
  onSubmit,
  onCancel,
}: SellerEditFormProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SellerEditValues>({
    resolver: zodResolver(sellerEditSchema),
    defaultValues: {
      business_name: seller.business_name,
      pavilion_number: seller.pavilion_number,
      phone_e164: seller.phone_e164,
      whatsapp_phone_e164: seller.whatsapp_phone_e164 ?? "",
      telegram_handle: seller.telegram_handle ?? "",
      entrance_hint: seller.entrance_hint ?? "",
      verification_status:
        SELLER_VERIFICATION_STATUSES.find(
          (s) => s === seller.verification_status,
        ) ?? "pending",
      verification_notes: seller.verification_notes ?? "",
    },
  });

  const submit = useCallback(
    (values: SellerEditValues) => {
      const body: AdminSellerUpdate = {
        business_name: values.business_name.trim(),
        pavilion_number: values.pavilion_number.trim(),
        phone_e164: values.phone_e164.trim(),
        whatsapp_phone_e164: nullIfBlank(values.whatsapp_phone_e164),
        telegram_handle: nullIfBlank(values.telegram_handle),
        entrance_hint: nullIfBlank(values.entrance_hint),
        verification_status: values.verification_status,
        verification_notes: nullIfBlank(values.verification_notes),
      };
      onSubmit(body);
    },
    [onSubmit],
  );

  const errText = (key?: string): string | undefined =>
    key ? t(key) : undefined;

  return (
    <form
      data-testid="seller-edit-form"
      onSubmit={handleSubmit(submit)}
      className="flex flex-col gap-4"
    >
      <Field
        label={t("sellers.edit.businessName")}
        data-testid="seller-edit-business_name"
        error={errText(errors.business_name?.message)}
        {...register("business_name")}
      />
      <Field
        label={t("sellers.edit.pavilion")}
        data-testid="seller-edit-pavilion_number"
        error={errText(errors.pavilion_number?.message)}
        {...register("pavilion_number")}
      />
      <Field
        label={t("sellers.edit.phone")}
        data-testid="seller-edit-phone_e164"
        error={errText(errors.phone_e164?.message)}
        {...register("phone_e164")}
      />
      <Field
        label={t("sellers.edit.whatsapp")}
        data-testid="seller-edit-whatsapp"
        error={errText(errors.whatsapp_phone_e164?.message)}
        {...register("whatsapp_phone_e164")}
      />
      <Field
        label={t("sellers.edit.telegram")}
        data-testid="seller-edit-telegram"
        {...register("telegram_handle")}
      />

      <div className="flex flex-col gap-1.5">
        <label htmlFor="seller-edit-status" className="text-[13px] font-semibold text-foreground">
          {t("sellers.edit.status")}
        </label>
        <select
          id="seller-edit-status"
          data-testid="seller-edit-verification_status"
          className={SELECT_CLASS}
          {...register("verification_status")}
        >
          {SELLER_VERIFICATION_STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`sellers.verification.${s}`)}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="seller-edit-hint" className="text-[13px] font-semibold text-foreground">
          {t("sellers.edit.entranceHint")}
        </label>
        <textarea
          id="seller-edit-hint"
          data-testid="seller-edit-entrance_hint"
          rows={2}
          maxLength={500}
          className="w-full resize-none rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]"
          {...register("entrance_hint")}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="seller-edit-notes" className="text-[13px] font-semibold text-foreground">
          {t("sellers.edit.notes")}
        </label>
        <textarea
          id="seller-edit-notes"
          data-testid="seller-edit-verification_notes"
          rows={2}
          maxLength={1000}
          className="w-full resize-none rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]"
          {...register("verification_notes")}
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={isPending}
          className="min-h-[44px]"
        >
          {t("common.cancel")}
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          data-testid="seller-edit-submit"
          loading={isPending}
          className="min-h-[44px]"
        >
          {t("sellers.edit.submit")}
        </Button>
      </div>
    </form>
  );
}

export default SellerEditForm;
